/**
 * CRUD data layer for the inserts table.
 * Used by the InsertsSettings admin page.
 * Inserts are short audio/image segments shown to users in stream responses.
 */
import type {
  Insert,
  CreateInsertRequest,
  UpdateInsertRequest,
} from '@musedrops/shared';
import { fromDbKeys, toDbKeys } from '@musedrops/shared';
import { supabase } from '../services/supabase';

export interface ListInsertsOptions {
  sortField?: keyof Insert | string;
  sortOrder?: 'ASC' | 'DESC';
}

export async function listInserts(
  options: ListInsertsOptions = {}
): Promise<Insert[]> {
  const { sortField = 'minStoriesPlayed', sortOrder = 'ASC' } = options;

  // Convert camelCase sortField to snake_case for DB query
  const dbSortField =
    sortField === 'minStoriesPlayed' ? 'min_stories_played' : String(sortField);

  const { data, error } = await supabase
    .from('inserts')
    .select('*')
    .order(dbSortField, { ascending: sortOrder === 'ASC' });

  if (error) {
    throw new Error(`Failed to fetch inserts: ${error.message ?? error.code ?? JSON.stringify(error)}`);
  }

  return (data ?? []).map(row =>
    fromDbKeys<Insert>(row as Record<string, unknown>)
  );
}

export async function getInsert(slug: string): Promise<Insert> {
  const { data, error } = await supabase
    .from('inserts')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error || !data) {
    throw new Error(`Insert not found: ${slug}`);
  }

  return fromDbKeys<Insert>(data as Record<string, unknown>);
}

export async function createInsert(
  insertData: CreateInsertRequest
): Promise<Insert> {
  const { data, error } = await supabase
    .from('inserts')
    .insert(toDbKeys(insertData))
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to create insert: ${error.message ?? error.code ?? JSON.stringify(error)}`);
  }

  return fromDbKeys<Insert>(data as Record<string, unknown>);
}

export async function updateInsert(
  slug: string,
  insertData: UpdateInsertRequest
): Promise<Insert> {
  const { data, error } = await supabase
    .from('inserts')
    .update(toDbKeys(insertData))
    .eq('slug', slug)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to update insert: ${error.message ?? error.code ?? JSON.stringify(error)}`);
  }

  return fromDbKeys<Insert>(data as Record<string, unknown>);
}

export async function deleteInsert(slug: string): Promise<void> {
  const { error } = await supabase.from('inserts').delete().eq('slug', slug);

  if (error) {
    throw new Error(`Failed to delete insert: ${error.message ?? error.code ?? JSON.stringify(error)}`);
  }
}
